import { FieldTypes } from "pages/employer-of-record/components/AutoForm";
import { getDescription } from "pages/employer-of-record/utils";
import { countryIs, field, isAdmin, personalInfo, REQUIRED } from "./utils";
import * as BRAZIL from "pages/employer-of-record/onboarding/profile-info/form/br/constants.br";
import { confirmTaxIdFieldConfig, taxIdFieldConfig } from "./_common";
import { Trans } from "react-i18next";

export const brazilFields = {
  brGender: field({
    type: FieldTypes.select,
    informationType: "identification",
    name: "brGender",
    label: "Gender",
    placeholder: "Select Gender",
    message:
      "For the time being, we are limited to these two options. We recognize that life is more nuanced and are actively exploring ways to fully reflect everyone's experiences.",
    options: BRAZIL.GENDER_OPTIONS,
    validations: REQUIRED,
    scenarios: {
      onboarding: countryIs("BR"),
    },
    value: personalInfo("brGender", ""),
    format: (value) => getDescription(BRAZIL.GENDER_OPTIONS, value) ?? value,
  }),
  brCityOfBirth: field({
    type: FieldTypes.text,
    informationType: "identification",
    name: "brCityOfBirth",
    label: "City of birth",
    validations: REQUIRED,
    profileSection: "personalInfo",
    scenarios: {
      editProfile: countryIs("BR"),
      viewProfile: countryIs("BR"),
      onboarding: countryIs("BR"),
    },
    value: personalInfo("brCityOfBirth", ""),
  }),
  brStateOfBirth: field({
    type: FieldTypes.select,
    informationType: "identification",
    name: "brStateOfBirth",
    label: "State of birth",
    placeholder: "Select State of Birth",
    validations: REQUIRED,
    options: BRAZIL.STATE_OPTIONS,
    profileSection: "personalInfo",
    scenarios: {
      editProfile: countryIs("BR"),
      viewProfile: countryIs("BR"),
      onboarding: countryIs("BR"),
    },
    value: personalInfo("brStateOfBirth", ""),
    format: (value) => getDescription(BRAZIL.STATE_OPTIONS, value) ?? value,
  }),
  brEthnicity: field({
    type: FieldTypes.select,
    informationType: "identification",
    name: "brEthnicity",
    label: "Ethnicity",
    placeholder: "Select Ethnicity",
    options: BRAZIL.ETHNICITY_OPTIONS,
    validations: REQUIRED,
    profileSection: "personalInfo",
    scenarios: {
      editProfile: countryIs("BR"),
      viewProfile: countryIs("BR"),
      onboarding: countryIs("BR"),
    },
    value: personalInfo("brEthnicity", ""),
    format: (value) => getDescription(BRAZIL.ETHNICITY_OPTIONS, value) ?? value,
  }),
  brWorkPermitStatus: field({
    type: FieldTypes.select,
    informationType: "identification",
    name: "brWorkPermitStatus",
    label: "Work permit status",
    placeholder: "Select Work Permit status",
    options: BRAZIL.WORK_PERMIT_OPTIONS,
    validations: REQUIRED,
    profileSection: "personalInfo",
    scenarios: {
      editProfile: (profile, authenticatedMember) =>
        profile.employment?.workCountry === "BR" &&
        isAdmin(profile, authenticatedMember),
      viewProfile: countryIs("BR"),
      onboarding: countryIs("BR"),
    },
    value: personalInfo("brWorkPermitStatus", ""),
    format: (value) =>
      getDescription(BRAZIL.WORK_PERMIT_OPTIONS, value) ?? value,
  }),
  brMaritalStatus: field({
    type: FieldTypes.select,
    informationType: "identification",
    name: "brMaritalStatus",
    label: "Marital status",
    placeholder: "Select Marital status",
    validations: REQUIRED,
    options: BRAZIL.MARITAL_STATUS_OPTIONS,
    profileSection: "personalInfo",
    scenarios: {
      editProfile: countryIs("BR"),
      viewProfile: countryIs("BR"),
      onboarding: countryIs("BR"),
    },
    value: personalInfo("brMaritalStatus", ""),
    format: (value) =>
      getDescription(BRAZIL.MARITAL_STATUS_OPTIONS, value) ?? value,
  }),
  brEducationalQualification: field({
    type: FieldTypes.text,
    informationType: "education",
    name: "brEducationalQualification",
    label: "Level of education",
    validations: REQUIRED,
    profileSection: "personalInfo",
    scenarios: {
      editProfile: (profile) => profile.employment?.workCountry === "BR",
      viewProfile: countryIs("BR"),
      onboarding: countryIs("BR"),
    },
    value: personalInfo("brEducationalQualification", ""),
  }),
  brPersonalId: field({
    type: FieldTypes.text,
    informationType: "rg",
    name: "brPersonalId",
    label: "ID Number (RG)",
    message:
      "Enter the RG number with dots and hyphen (e.g. 12.345.678-9), 10 digits and hyphen (e.g. 1234567890-x), or 10 digits only (e.g. 1234567890)",
    validations: {
      ...REQUIRED,
      text: {
        matches: {
          value: BRAZIL.brPersonalIdRegEx,
          message: "Must be a valid Personal ID.",
        },
      },
    },
    scenarios: {
      viewProfile: countryIs("BR"),
      onboarding: countryIs("BR"),
    },
    value: personalInfo("brPersonalId", ""),
  }),
  brRgStateOfIssue: field({
    type: FieldTypes.select,
    informationType: "rg",
    name: "brRgStateOfIssue",
    label: "RG state of issue",
    placeholder: "Select state of issue",
    options: BRAZIL.STATE_OPTIONS,
    validations: REQUIRED,
    scenarios: {
      viewProfile: countryIs("BR"),
      onboarding: countryIs("BR"),
    },
    value: personalInfo("brRgStateOfIssue", ""),
  }),
  brRgIssuingBody: field({
    type: FieldTypes.text,
    informationType: "rg",
    name: "brRgIssuingBody",
    label: "RG issuing body",
    validations: REQUIRED,
    scenarios: {
      viewProfile: countryIs("BR"),
      onboarding: countryIs("BR"),
    },
    value: personalInfo("brRgIssuingBody", ""),
  }),
  brUnionContribution: field({
    type: FieldTypes.checkbox,
    informationType: "job",
    name: "brUnionContribution",
    label: "Do you agree to contribute to the union welfare contribution?",
    description: "Union welfare contribution agreement",
    notice: {
      // t("brUnionContribution.notice.title")
      title: "brUnionContribution.notice.title",
      message: (
        <Trans
          i18nKey="brUnionContribution.notice.message"
          components={{
            strong: <strong />,
          }}
        />
      ),
    },
    scenarios: {
      onboarding: countryIs("BR"),
    },
    value: () => true,
  }),
  brCpf: taxIdFieldConfig("BR", "brCpf"),
  brConfirmCpf: confirmTaxIdFieldConfig("BR", "brCpf"),
};
